import { useCallback, useEffect, useMemo, useState } from 'react';
import { apiPath } from '../lib/http';

const ENTITLEMENTS_TIMEOUT_MS = 10000;
const UNAUTHORIZED_STATUSES = new Set([401, 403]);

function entitlementFetchError(message, details = {}) {
  const error = new Error(message);
  error.status = details.status;
  error.retryable = details.retryable !== false;
  return error;
}

/** Always-visible nav destinations (no module lock). */
export const OPEN_NAV_IDS = new Set([
  'overview',
  'websites',
  'create-template',
  'platform-guide',
  'odoo-erp',
  'subscription-plans',
  'settings',
  'support',
]);

/** Sidebar nav id → required module id(s). */
export const NAV_MODULE_MAP = {
  snagbuilder: ['snag_builder'],
  alifullstack: ['snag_builder'],
  'ai-cofounder': ['ai_cofounder'],
  'ai-tools': ['ai_tools_hub'],
  'media-studio': ['media_studio'],
  'bot-leads': ['bot_leads_calls'],
  voice: ['voice_campaigns'],
  domains: ['domains'],
  marketing: ['marketing_suite'],
  'ai-seo': ['ai_seo'],
  'ai-ads': ['ai_ads', 'ai_social_media'],
  crm: ['marketing_crm'],
  automations: ['automations'],
  connections: ['connections'],
  'ai-studio': ['ai_studio'],
  'lead-scraper': ['lead_scraper'],
  support: ['support'],
};

/** Route path prefix → required module id(s). Longer prefixes should be listed first. */
export const ROUTE_MODULE_RULES = [
  ['/snagbuilder', ['snag_builder']],
  ['/alifullstack', ['snag_builder']],
  ['/ai-cofounder', ['ai_cofounder']],
  ['/ai-tools-dashboard', ['ai_tools_hub']],
  // Premium AI tool add-ons (must be before generic /ai-tool/)
  ['/ai-tool/detector', ['ai_detector']],
  ['/ai-tool/plagiarism', ['ai_plagiarism']],
  ['/ai-tool/youtube', ['ai_youtube']],
  ['/ai-tool/voice-bot', ['voice_campaigns']],
  ['/ai-tool/', ['ai_tools_hub']],
  ['/media-studio', ['media_studio']],
  ['/ai-presentation', ['ai_presentation']],
  // Legacy web-agent surface — unlock with Snag Builder / websites (not a separate SKU)
  ['/ai-web-agent', ['snag_builder', 'websites']],
  ['/domains-hosting', ['domains']],
  ['/marketing-suite/email', ['marketing_email']],
  ['/marketing-suite/sms', ['marketing_sms']],
  ['/marketing-suite/whatsapp', ['marketing_whatsapp']],
  ['/marketing-suite/crm', ['marketing_crm']],
  ['/marketing-suite/seo', ['ai_seo']],
  // Social scheduler lives here; accept legacy ai_social_media entitlements too
  ['/marketing-suite/ads', ['ai_ads', 'ai_social_media']],
  ['/marketing-suite', ['marketing_suite']],
  ['/lead-scraper', ['lead_scraper']],
  ['/automations', ['automations']],
  ['/connections', ['connections']],
  ['/ai-studio', ['ai_studio']],
  ['/bot-leads', ['bot_leads_calls']],
  ['/voice-campaigns', ['voice_campaigns']],
  ['/support', ['support']],
  ['/app-builder-existing', ['snag_builder']],
  ['/ai-engineer', ['snag_builder']],
  ['/app-builder', ['snag_builder']],
  ['/gpt-pilot', ['snag_builder']],
  ['/metagpt', ['snag_builder']],
];

/** AI Tools dashboard tool id → catalog module (add-ons require purchase). */
export const AI_TOOL_MODULE_MAP = {
  writer: 'ai_writer',
  editor: 'ai_editor',
  rewriter: 'ai_rewriter',
  'article-wizard': 'ai_article_wizard',
  chat: 'ai_chat',
  code: 'ai_code',
  'chat-image': 'ai_chat_image',
  'product-photo': 'ai_product_photo',
  voiceover: 'ai_voiceover',
  'speech-to-text': 'ai_speech_to_text',
  'file-chat': 'ai_file_chat',
  'web-chat': 'ai_web_chat',
  presentation: 'ai_presentation',
  'social-media': 'ai_social_media',
  'smart-bot': 'ai_smart_bot',
  avatar: 'media_studio',
  vision: 'media_studio',
  detector: 'ai_detector',
  plagiarism: 'ai_plagiarism',
  youtube: 'ai_youtube',
  // Same product as Voice Campaigns (left nav) — not a separate add-on
  'voice-bot': 'voice_campaigns',
  rss: 'ai_tools_hub',
  'brand-voice': 'ai_tools_hub',
};

export const AI_TOOL_ADDON_IDS = new Set([
  'ai_detector',
  'ai_plagiarism',
  'ai_youtube',
]);

export function moduleForAiTool(toolId) {
  if (!toolId) return 'ai_tools_hub';
  return AI_TOOL_MODULE_MAP[toolId] || 'ai_tools_hub';
}

let _cache = null;
let _inflight = null;

export async function fetchEntitlements(force = false) {
  if (!force && _cache) return _cache;
  if (!force && _inflight) return _inflight;
  const token = localStorage.getItem('token');
  if (!token) {
    _cache = null;
    return null;
  }
  _inflight = (async () => {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), ENTITLEMENTS_TIMEOUT_MS);
    try {
      const res = await fetch(apiPath('/subscription/entitlements'), {
        headers: { Authorization: `Bearer ${token}` },
        signal: controller.signal,
      });
      if (UNAUTHORIZED_STATUSES.has(res.status)) {
        _cache = null;
        return null;
      }
      if (!res.ok) {
        throw entitlementFetchError('Unable to check access right now.', { status: res.status });
      }
      const data = await res.json();
      _cache = data;
      return data;
    } catch (error) {
      if (error.name === 'AbortError') {
        throw entitlementFetchError('Access check timed out. Please retry.');
      }
      if (error.retryable) throw error;
      throw entitlementFetchError('Unable to check access right now.');
    } finally {
      clearTimeout(timeout);
      _inflight = null;
    }
  })();
  return _inflight;
}

export function clearEntitlementsCache() {
  _cache = null;
}

export function hasModule(entitlements, moduleId) {
  if (!entitlements) return false; // fail-closed until entitlements load
  const ids = entitlements.activated_module_ids || [];
  return ids.includes(moduleId);
}

export function hasAnyModule(entitlements, moduleIds = []) {
  if (!moduleIds.length) return true;
  return moduleIds.some((id) => hasModule(entitlements, id));
}

export function modulesForPath(pathname) {
  for (const [prefix, mods] of ROUTE_MODULE_RULES) {
    if (pathname === prefix || pathname.startsWith(prefix)) return mods;
  }
  return null;
}

export default function useEntitlements() {
  const [entitlements, setEntitlements] = useState(_cache);
  const [loading, setLoading] = useState(!_cache);
  const [error, setError] = useState(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await fetchEntitlements(true);
      setEntitlements(data);
      return data;
    } catch (err) {
      setError(err);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await fetchEntitlements(false);
        if (!cancelled) {
          setEntitlements(data);
          setError(null);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const activated = useMemo(
    () => new Set(entitlements?.activated_module_ids || []),
    [entitlements],
  );

  const can = useCallback(
    (moduleId) => {
      if (!entitlements) return false;
      return activated.has(moduleId);
    },
    [activated, entitlements],
  );

  const canAny = useCallback(
    (moduleIds = []) => {
      if (!moduleIds.length) return true;
      if (!entitlements) return false;
      return moduleIds.some((id) => activated.has(id));
    },
    [activated, entitlements],
  );

  return {
    entitlements,
    loading,
    error,
    refresh,
    can,
    canAny,
    plan: entitlements?.plan || null,
    limits: entitlements?.limits || null,
    activatedModuleIds: entitlements?.activated_module_ids || [],
  };
}
